import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components';
import * as styles from './DashboardFilters.module.css';

function DashboardFilters({ filters, onFiltersChange, availableEntities = {} }) {
  const { t } = useTranslation();
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [showEntitySelector, setShowEntitySelector] = useState(null);
  const [selectedMetricHost, setSelectedMetricHost] = useState(null);

  const TIME_RANGES = [
    { value: 'last_24h', label: t('dashboard.filters.lastHour'), hours: 24 },
    { value: 'last_7d', label: t('dashboard.filters.lastDay'), hours: 24 * 7 },
    { value: 'last_30d', label: t('dashboard.filters.lastWeek'), hours: 24 * 30 },
    { value: 'last_90d', label: t('dashboard.filters.lastMonth'), hours: 24 * 90 },
    { value: 'last_365d', label: t('dashboard.filters.lastYear'), hours: 24 * 365 },
    { value: 'custom', label: t('dashboard.filters.custom'), hours: null },
  ];

  const ENTITY_TYPES = [
    { value: 'hosts', label: t('dashboard.filters.entities.hosts') },
    { value: 'metrics', label: t('dashboard.filters.entities.metrics') },
    { value: 'business_services', label: t('dashboard.filters.entities.businessServices') },
    { value: 'host_groups', label: t('dashboard.filters.entities.hostGroups') },
  ];

  const handleTimeRangeChange = (value) => {
    if (value === 'custom') {
      setShowCustomPicker(true);
      return;
    }

    const range = TIME_RANGES.find(r => r.value === value);
    if (range) {
      const now = new Date();
      const from = new Date(now.getTime() - (range.hours * 60 * 60 * 1000));
      
      onFiltersChange({
        ...filters,
        timeRange: value,
        timeFrom: Math.floor(from.getTime() / 1000),
        timeTo: Math.floor(now.getTime() / 1000),
      });
    }
    setShowCustomPicker(false);
  };

  const handleCustomTimeApply = () => {
    // Custom time is already set via the input changes
    onFiltersChange({
      ...filters,
      timeRange: 'custom',
    });
    setShowCustomPicker(false);
  };

  const handleEntityTypeToggle = (entityType) => {
    if (showEntitySelector === entityType) {
      setShowEntitySelector(null);
    } else {
      setShowEntitySelector(entityType);
    }
  };

  const handleEntitySelect = (entityType, entityId) => {
    const currentEntities = filters.entities?.[entityType] || [];
    const isSelected = currentEntities.includes(entityId);
    
    const newEntities = isSelected
      ? currentEntities.filter(id => id !== entityId)
      : [...currentEntities, entityId];
    
    onFiltersChange({
      ...filters,
      entities: {
        ...filters.entities,
        [entityType]: newEntities,
      },
    });
  };

  const clearEntityType = (entityType) => {
    onFiltersChange({
      ...filters,
      entities: {
        ...filters.entities,
        [entityType]: [],
      },
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      timeRange: null,
      timeFrom: null,
      timeTo: null,
      entities: {
        hosts: [],
        metrics: [],
        business_services: [],
        host_groups: [],
      },
    });
    setShowCustomPicker(false);
    setShowEntitySelector(null);
  };

  const hasActiveFilters = filters.timeRange || 
    Object.values(filters.entities || {}).some(arr => arr.length > 0);

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('dashboard.timeRange')}</h3>
        <div className={styles.timeButtons}>
          {TIME_RANGES.map(range => (
            <button
              key={range.value}
              type="button"
              className={`${styles.timeButton} ${filters.timeRange === range.value ? styles.active : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleTimeRangeChange(range.value);
              }}
            >
              {range.label}
            </button>
          ))}
        </div>
        
        {showCustomPicker && (
          <div className={styles.customPicker}>
            <div className={styles.customPickerRow}>
              <label htmlFor="timeFrom" className={styles.label}>
                {t('dashboard.from')}:
                <input
                  id="timeFrom"
                  type="datetime-local"
                  value={filters.timeFrom ? new Date(filters.timeFrom * 1000).toISOString().slice(0, 16) : ''}
                  onChange={(e) => onFiltersChange({
                    ...filters,
                    timeFrom: e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null,
                  })}
                  className={styles.dateInput}
                />
              </label>
            </div>
            <div className={styles.customPickerRow}>
              <label htmlFor="timeTo" className={styles.label}>
                {t('dashboard.to')}:
                <input
                  id="timeTo"
                  type="datetime-local"
                  value={filters.timeTo ? new Date(filters.timeTo * 1000).toISOString().slice(0, 16) : ''}
                  onChange={(e) => onFiltersChange({
                    ...filters,
                    timeTo: e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null,
                  })}
                  className={styles.dateInput}
                />
              </label>
            </div>
            <Button 
              size="small" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCustomTimeApply();
              }}
            >
              {t('dashboard.applyCustomRange')}
            </Button>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('dashboard.filterByEntities')}</h3>
        <div className={styles.entityButtons}>
          {ENTITY_TYPES.map(entityType => {
            const selectedCount = (filters.entities?.[entityType.value] || []).length;
            
            return (
              <div key={entityType.value} className={styles.entityGroup}>
                <button
                  type="button"
                  className={`${styles.entityButton} ${selectedCount > 0 ? styles.active : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEntityTypeToggle(entityType.value);
                  }}
                >
                  {entityType.label}
                  {selectedCount > 0 && (
                    <span className={styles.badge}>{selectedCount}</span>
                  )}
                </button>
                
                {showEntitySelector === entityType.value && (
                  <div className={styles.entityDropdown}>
                    <div className={styles.dropdownHeader}>
                      <span>{t('dashboard.selectEntities')} {entityType.label}</span>
                      {selectedCount > 0 && (
                        <button
                          type="button"
                          className={styles.clearButton}
                          onClick={() => clearEntityType(entityType.value)}
                        >
                          {t('dashboard.clearButton')}
                        </button>
                      )}
                    </div>
                    
                    {/* Cascading selector for metrics (host -> metrics) */}
                    {entityType.value === 'metrics' ? (
                      <div className={styles.cascadingSelector}>
                        {/* Step 1: Select Host */}
                        <div className={styles.hostSelector}>
                          <div className={styles.selectorLabel}>{t('dashboard.filters.entities.selectHost')}</div>
                          <select
                            className={styles.hostSelect}
                            value={selectedMetricHost || ''}
                            onChange={(e) => setSelectedMetricHost(e.target.value || null)}
                          >
                            <option value="">{t('dashboard.chooseHost')}</option>
                            {(availableEntities.hosts || []).map(host => (
                              <option key={host.id} value={host.name}>
                                {host.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Step 2: Select Metrics for chosen host */}
                        {selectedMetricHost && (
                          <div className={styles.metricSelector}>
                            <div className={styles.selectorLabel}>{t('dashboard.filters.entities.selectMetrics')}</div>
                            <div className={styles.entityList}>
                              {(availableEntities.metrics || [])
                                .filter(metric => metric.hostName === selectedMetricHost)
                                .map((metric) => {
                                  // Create unique key using id and hostName to avoid duplicates
                                  const uniqueKey = `metric-${metric.id}-${metric.hostName || ''}`;
                                  return (
                                  <label key={uniqueKey} className={styles.entityItem} htmlFor={uniqueKey}>
                                    <input
                                      id={uniqueKey}
                                      type="checkbox"
                                      checked={(filters.entities?.metrics || []).includes(metric.id)}
                                      onChange={() => handleEntitySelect('metrics', metric.id)}
                                    />
                                    <span>{metric.name}</span>
                                  </label>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Simple checkbox selector for other entity types */
                      <div className={styles.entityList}>
                        {(availableEntities[entityType.value] || []).length > 0 ? (
                          (availableEntities[entityType.value] || []).map((entity) => {
                            // Create unique key using entity type and id (names can be duplicated)
                            const uniqueKey = `${entityType.value}-${entity.id}`;
                            return (
                            <label key={uniqueKey} className={styles.entityItem} htmlFor={uniqueKey}>
                              <input
                                id={uniqueKey}
                                type="checkbox"
                                checked={(filters.entities?.[entityType.value] || []).includes(entity.id)}
                                onChange={() => handleEntitySelect(entityType.value, entity.id)}
                              />
                              <span>{entity.name}</span>
                            </label>
                            );
                          })
                        ) : (
                          <div className={styles.noEntities}>
                            {t('dashboard.filters.entities.noEntitiesAvailable', { type: entityType.label.toLowerCase() })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {hasActiveFilters && (
        <div className={styles.actions}>
          <Button 
            variant="outline" 
            size="small" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearAllFilters();
            }}
          >
            {t('dashboard.clearAll')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default DashboardFilters;

